import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, DollarSign, BarChart3, TrendingDown, AlertTriangle, ChevronDown, MapPin, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LandingHeader } from '../../components/landing/LandingHeader';
import { CTASection } from '../../components/landing/CTASection';

export default function LaborCost() {
  useEffect(() => {
    document.title = 'Labor Cost Management Software | ReStaff';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = 'Monitor, control, and reduce labor costs with real-time workforce insights.';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="labor-cost-page">
      <LandingHeader />

      {/* HERO */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight" style={{ fontFamily: 'Manrope' }}>
            Labor Cost Management Software
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Labor cost management software is a key component of workforce management systems that helps businesses monitor, control, and optimize employee-related expenses.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white text-base px-8" data-testid="labor-hero-cta">
                Start Free Trial <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
          <p className="mt-3 text-sm text-slate-400">30-day free trial &middot; Cancel anytime, no questions asked</p>
        </div>
      </section>

      {/* WHAT IT DOES */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-5" style={{ fontFamily: 'Manrope' }}>
            What is Labor Cost Management?
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            Labor cost management is the practice of monitoring and controlling employee-related expenses — wages, overtime, and staffing levels — to ensure profitability. ReStaff gives managers real-time visibility into what labor is costing across every location, so overspending is caught during the week, not after payroll.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: '1', title: 'Data Flows In', desc: 'Schedules, clock events, and wage rates feed into ReStaff automatically. No manual data entry required.', icon: Clock },
              { step: '2', title: 'Costs Are Calculated', desc: 'ReStaff calculates labor costs in real time — by employee, location, day, and week. Budget vs. actual is always visible.', icon: DollarSign },
              { step: '3', title: 'Insights Drive Action', desc: 'Managers get alerts for overtime risks and budget overruns. Use insights to adjust schedules before costs escalate.', icon: BarChart3 },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.step} className="bg-white border border-slate-100 rounded-xl p-6 text-center" data-testid={`labor-step-${s.step}`}>
                  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-4">
                    <Icon size={20} className="text-orange-500" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* KEY FEATURES */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Labor Cost Features
          </h2>
          <div className="grid sm:grid-cols-2 gap-5 md:gap-8">
            {[
              { title: 'Real-Time Labor Cost Tracking', desc: 'See what labor is costing you right now — not at the end of the pay period. Costs update as employees clock in and work their shifts.', icon: DollarSign },
              { title: 'Budget vs. Actual Comparison', desc: 'Set weekly labor budgets per location. ReStaff shows budget vs. actual spend so managers know when they are trending over.', icon: BarChart3 },
              { title: 'Overtime Alerts', desc: 'Get notified when employees approach overtime thresholds. Adjust schedules proactively to prevent costly overages.', icon: AlertTriangle },
              { title: 'Cost Forecasting', desc: 'See projected labor costs for the week based on the published schedule. Identify potential overruns before they happen.', icon: TrendingDown },
              { title: 'Multi-Location Comparison', desc: 'Compare labor costs, hours, and efficiency across all locations. Identify which branches are overspending and why.', icon: MapPin },
              { title: 'Cost-Per-Employee Breakdown', desc: 'See exactly how much each employee costs — including regular hours, overtime, and total wages — for any date range.', icon: Clock },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white border border-slate-100 rounded-xl p-6 hover:border-orange-200 hover:shadow-sm transition-all" data-testid={`labor-feature-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center mb-4">
                    <Icon size={20} className="text-orange-500" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Why Use ReStaff for Labor Cost Management
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Reduce labor costs by catching overages in real time',
              'Save money by preventing unplanned overtime',
              'Improve scheduling efficiency based on cost data',
              'Get full visibility into labor spend across every location',
              'Make data-driven staffing decisions, not guesses',
              'Forecast labor costs before the week starts',
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl p-4">
                <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-700">{b}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-slate-500 text-center leading-relaxed">
            Labor insights work best with ReStaff <Link to="/employee-scheduling" className="text-orange-500 hover:underline font-medium">employee scheduling software</Link>, <Link to="/time-tracking" className="text-orange-500 hover:underline font-medium">time tracking</Link>, <Link to="/timesheet-software" className="text-orange-500 hover:underline font-medium">timesheet software</Link>, and <Link to="/task-management" className="text-orange-500 hover:underline font-medium">task management software</Link>. Used by <Link to="/restaurants" className="text-orange-500 hover:underline font-medium">restaurants</Link>, <Link to="/retail" className="text-orange-500 hover:underline font-medium">retail stores</Link>, and more.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {[
              { q: 'How do I reduce labor costs in my business?', a: 'Start by tracking labor costs in real time so you can see where money is going. Use scheduling tools to match staffing to demand, set overtime alerts, and compare costs across locations. ReStaff automates all of this.' },
              { q: 'What is labor cost percentage?', a: 'Labor cost percentage is your total labor expense divided by revenue, expressed as a percentage. It tells you how much of every dollar earned goes to paying employees. ReStaff tracks this metric in the Executive Insights dashboard.' },
              { q: 'How do I track labor costs in real time?', a: 'ReStaff calculates labor costs continuously as employees clock in and work their shifts. Managers see current spend vs. budget at any point during the week — no waiting for payroll.' },
              { q: 'Can I compare labor costs across locations?', a: 'Yes. ReStaff provides location-level labor cost reporting. Compare spend, hours, overtime, and efficiency across all branches from the Executive Insights dashboard.' },
            ].map((item, i) => (
              <details key={i} className="group bg-slate-50 border border-slate-100 rounded-xl" data-testid={`labor-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer p-5 text-sm font-semibold text-slate-900 select-none">
                  {item.q}
                  <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180 flex-shrink-0 ml-3" />
                </summary>
                <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
